import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle, FileUp, RefreshCw, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export const PrescriptionDocumentModal = ({
  open,
  onOpenChange,
  prescription,
  onSuccess,
  onConflict
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [conflictError, setConflictError] = useState(null);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(selected.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG');
      return;
    }

    if (selected.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds maximum 10MB limit');
      return;
    }

    setFile(selected);
    setConflictError(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setConflictError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('expected_version', String(prescription?.version || 1));

    try {
      const res = await api.put(`/prescriptions/${prescription.id}/document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const data = res.data?.data || res.data;
      toast.success('Prescription document replaced successfully');
      setFile(null);
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to replace prescription document';

      if (status === 409) {
        setConflictError(detail);
      } else if (status === 400 && detail.includes('immutable')) {
        toast.error('This prescription has been completed or cancelled and its document can no longer be replaced.');
        onOpenChange(false);
        onConflict?.();
      } else {
        toast.error(detail);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleReload = () => {
    onConflict?.();
    onOpenChange(false);
  };

  const hasExistingDoc = Boolean(prescription?.file_url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <FileUp className="h-5 w-5 text-emerald-700" />
            {hasExistingDoc ? 'Replace Prescription Document' : 'Upload Prescription Document'}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {hasExistingDoc
              ? 'Upload an updated scan or digital PDF before prescription completion. The previous document will remain preserved in historical audit logs.'
              : 'Attach a digital PDF or prescription image scan to this prescription record.'}
          </DialogDescription>
        </DialogHeader>

        {conflictError && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2.5 text-amber-900 text-sm">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-xs text-amber-950">Concurrency Conflict</p>
              <p className="mt-0.5 text-xs text-amber-800">{conflictError}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleReload}
                className="mt-2 text-xs h-7 border-amber-400 bg-white hover:bg-amber-100 text-amber-900"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Reload Latest Version
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-4 pt-1">
          <div>
            <Label className="text-xs font-semibold block mb-1.5">
              {hasExistingDoc ? 'Select New Document (PDF, JPG, PNG - Max 10MB)' : 'Select Document (PDF, JPG, PNG - Max 10MB)'}
            </Label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-5 text-center hover:border-emerald-500 transition-colors bg-slate-50/50">
              <input
                type="file"
                id="doc-replace-file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="doc-replace-file"
                className="cursor-pointer flex flex-col items-center justify-center space-y-2"
              >
                <UploadCloud className="h-8 w-8 text-slate-400" />
                <span className="text-xs font-medium text-slate-700">
                  {file ? file.name : 'Click to choose replacement file'}
                </span>
                {file && (
                  <span className="text-[11px] text-muted-foreground">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                )}
              </label>
            </div>
          </div>

          <DialogFooter className="pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!file || uploading}
              className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <FileUp className="h-4 w-4" /> {hasExistingDoc ? 'Replace Document' : 'Upload Document'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionDocumentModal;
